/**
 * 用户权益管理器
 * 统一管理用户权益数据获取、缓存和弹窗逻辑
 */

import { enhancedRequest } from './enhancedRequest';
import { ApiResponse } from './request';
import { UserBenefitsResponse, BenefitItem, BenefitType } from '@/services/types';

// 存储键常量
const STORAGE_KEYS = {
  // 分离的福利数据存储键
  WELCOME_BENEFITS_DATA: 'amai_welcome_benefits_data', // type 1: 首次注册福利
  DAILY_BENEFITS_DATA: 'amai_daily_benefits_data', // type 2: 每日登录福利
  MONTHLY_BENEFITS_DATA: 'amai_monthly_benefits_data', // type 3: 每月福利

  // 弹窗状态存储键
  WELCOME_GIFT_SHOWN: 'amai_welcome_gift_shown',
  DAILY_REWARD_TIMESTAMP: 'amai_daily_reward_timestamp',
  MONTHLY_REWARD_TIMESTAMP: 'amai_monthly_reward_timestamp',
  PAYMENT_REWARD_TIMESTAMP: 'amai_payment_reward_timestamp', // 新增：支付奖励时间戳
  PHONE_VERIFICATION_REWARD_SHOWN: 'amai_phone_verification_reward_shown', // 新增：手机号验证奖励时间戳
} as const;

// 单个福利类型的缓存数据接口
interface CachedBenefitData {
  data: BenefitItem[];
  timestamp: number;
  ttl: number;
}

// 弹窗状态接口
interface ModalState {
  showWelcomeGift: boolean;
  showDailyReward: boolean;
  showMonthlyReward: boolean;
  showPaymentReward: boolean; // 新增：支付完成奖励
  showPhoneVerificationReward: boolean; // 新增：手机号验证完成奖励
  benefitsData: BenefitItem[] | null;
}

/**
 * 用户权益管理器类
 */
export class UserBenefitsManager {
  private static instance: UserBenefitsManager | null = null;
  private readonly cacheTTL = 24 * 60 * 60 * 1000; // 24小时缓存
  private isRequesting = false;

  private constructor() {}

  /**
   * 获取单例实例
   */
  public static getInstance(): UserBenefitsManager {
    if (!UserBenefitsManager.instance) {
      UserBenefitsManager.instance = new UserBenefitsManager();
    }
    return UserBenefitsManager.instance;
  }

  /**
   * 获取用户权益数据（带缓存）
   */
  public async getUserBenefits(forceRefresh = false): Promise<UserBenefitsResponse | null> {
    try {
      // 防止重复请求
      if (this.isRequesting && !forceRefresh) {
        console.log('[用户权益] 请求进行中，跳过重复请求');
        return null;
      }

      // 检查网络连接
      if (!navigator.onLine && !forceRefresh) {
        console.warn('[用户权益] 网络离线，尝试使用缓存数据');
        const cachedData = this.getAllCachedBenefitsData();
        if (cachedData) {
          return cachedData;
        }
        throw new Error('网络离线且无可用缓存');
      }

      // 尝试从缓存获取
      if (!forceRefresh) {
        const cachedData = this.getAllCachedBenefitsData();
        if (cachedData) {
          console.log('[用户权益] 使用缓存数据');
          return cachedData;
        }
      }

      this.isRequesting = true;
      console.log('[用户权益] 开始获取用户权益数据');

      // 检查网络状态
      if (!navigator.onLine) {
        throw new Error('网络连接不可用');
      }

      // 添加超时处理
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('获取权益数据超时')), 15000);
      });

      const fetchPromise = enhancedRequest.post<UserBenefitsResponse>(
        '/user/benefits',
        {},
        {
          enableDeduplication: true,
          requestKey: 'user-benefits',
          retryCount: 2,
          timeout: 10000,
        }
      );

      // 使用Promise.race处理超时
      const response: ApiResponse<UserBenefitsResponse> = await Promise.race([
        fetchPromise,
        timeoutPromise,
      ]);

      // 增强响应验证
      if (response && typeof response === 'object' && response.code === 0 && response.data) {
        // 按类型分别缓存成功的响应
        this.setCachedBenefitsDataByType(response.data);
        console.log('[用户权益] 权益数据获取成功', response.data);
        return response.data;
      } else {
        const errorMessage = response?.message || '未知错误';
        console.warn('[用户权益] 权益数据获取失败:', errorMessage);

        // 如果API返回错误但有缓存，使用缓存数据
        const cachedData = this.getAllCachedBenefitsData();
        if (cachedData) {
          console.log('[用户权益] API请求失败，使用缓存数据');
          return cachedData;
        }

        return null;
      }
    } catch (error) {
      // 详细的错误日志
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[用户权益] 获取权益数据异常:', {
        error: errorMessage,
        stack: error instanceof Error ? error.stack : undefined,
        type: typeof error,
      });

      // 异常情况下尝试使用缓存
      const cachedData = this.getAllCachedBenefitsData();
      if (cachedData) {
        console.log('[用户权益] 异常情况下使用缓存数据');
        return cachedData;
      }

      // 如果没有缓存数据，返回空的权益响应而不是null
      console.warn('[用户权益] 无缓存数据可用，返回空权益列表');
      return { list: [] };
    } finally {
      this.isRequesting = false;
    }
  }

  /**
   * 检查并获取需要显示的弹窗状态
   * @param forcePaymentReward 是否强制检查支付奖励（用于支付完成后）
   */
  public async checkModalState(forcePaymentReward = false): Promise<ModalState> {
    // 首先检查是否已经标记为已显示，如果是则直接返回不显示
    const welcomeShown = this.getWelcomeGiftShownStatus();
    const isTodayFirstVisit = this.isTodayFirstVisit();
    const isThisMonthFirstVisit = this.isThisMonthFirstVisit();

    // 如果是每月首次访问或支付奖励，需要强制刷新数据
    const shouldForceRefresh = forcePaymentReward || isThisMonthFirstVisit;

    // 如果所有弹窗都已经显示过且不需要强制刷新，直接返回
    if (welcomeShown && !isTodayFirstVisit && !isThisMonthFirstVisit && !forcePaymentReward) {
      console.log('[用户权益] 所有弹窗都已显示过，跳过检查');
      return {
        showWelcomeGift: false,
        showDailyReward: false,
        showMonthlyReward: false,
        showPaymentReward: false,
        showPhoneVerificationReward: false,
        benefitsData: null,
      };
    }

    // 获取权益数据，如果是每月首次访问或支付完成，强制刷新
    if (shouldForceRefresh) {
      console.log('[用户权益] 强制从服务端获取最新权益数据', {
        isMonthlyFirstVisit: isThisMonthFirstVisit,
        isPaymentReward: forcePaymentReward,
      });
    }

    const benefitsData = await this.getUserBenefits(shouldForceRefresh);

    if (!benefitsData) {
      console.log('[用户权益] 未获取到权益数据');
      return {
        showWelcomeGift: false,
        showDailyReward: false,
        showMonthlyReward: false,
        showPaymentReward: false,
        showPhoneVerificationReward: false,
        benefitsData: null,
      };
    }

    // 确保list字段存在
    const benefitsList = benefitsData.list || [];

    if (benefitsList.length === 0) {
      console.log('[用户权益] 权益列表为空');
      return {
        showWelcomeGift: false,
        showDailyReward: false,
        showMonthlyReward: false,
        showPaymentReward: false,
        showPhoneVerificationReward: false,
        benefitsData: [],
      };
    }

    const showWelcomeGift = this.shouldShowWelcomeGift(benefitsList);
    const showDailyReward = this.shouldShowDailyReward(benefitsList);
    const showMonthlyReward = this.shouldShowMonthlyReward(benefitsList, forcePaymentReward);
    const showPaymentReward = false; // 暂时设为false，后续会添加支付奖励逻辑
    const showPhoneVerificationReward = false; // 暂时设为false，后续会添加手机号验证奖励逻辑

    console.log('[用户权益] 弹窗状态检查结果:', {
      benefitsCount: benefitsList.length,
      showWelcomeGift,
      showDailyReward,
      showMonthlyReward,
      showPaymentReward,
      showPhoneVerificationReward,
    });

    return {
      showWelcomeGift,
      showDailyReward,
      showMonthlyReward,
      showPaymentReward,
      showPhoneVerificationReward,
      benefitsData: benefitsList,
    };
  }

  /**
   * 判断是否应该显示欢迎礼包弹窗
   */
  private shouldShowWelcomeGift(benefits: BenefitItem[]): boolean {
    // 安全检查
    if (!benefits || benefits.length === 0) {
      console.log('[用户权益] 无权益数据，不显示欢迎礼包');
      return false;
    }

    // 检查是否已经显示过 - 优先检查，避免不必要的处理
    const hasShown = this.getWelcomeGiftShownStatus();
    if (hasShown) {
      console.log('[用户权益] 欢迎礼包已显示过，不再显示');
      return false;
    }

    // 检查是否有首次注册权益（type=1）
    const welcomeGifts = benefits.filter(benefit => benefit.type === BenefitType.WELCOME_GIFT);
    if (welcomeGifts.length === 0) {
      console.log('[用户权益] 无首次注册权益，不显示欢迎礼包');
      return false;
    }

    // 验证权益数据的有效性（积分值大于0）
    const validWelcomeGifts = welcomeGifts.filter(gift => gift.credits > 0);
    if (validWelcomeGifts.length === 0) {
      console.log('[用户权益] 首次注册权益积分无效，不显示欢迎礼包');
      return false;
    }

    console.log(
      '[用户权益] 符合显示欢迎礼包条件，权益积分:',
      validWelcomeGifts.map(g => g.credits)
    );
    return true;
  }

  /**
   * 判断是否应该显示每日奖励弹窗
   */
  private shouldShowDailyReward(benefits: BenefitItem[]): boolean {
    // 安全检查
    if (!benefits || benefits.length === 0) {
      console.log('[用户权益] 无权益数据，不显示每日奖励');
      return false;
    }

    // 检查是否有每日登录权益（type=2）
    const hasDailyReward = benefits.some(benefit => benefit.type === BenefitType.DAILY_REWARD);
    if (!hasDailyReward) {
      console.log('[用户权益] 无每日登录权益，不显示每日奖励');
      return false;
    }

    // 检查是否是今天首次访问
    const isFirstVisit = this.isTodayFirstVisit();
    if (!isFirstVisit) {
      console.log('[用户权益] 今日已访问过，不显示每日奖励');
      return false;
    }

    console.log('[用户权益] 符合显示每日奖励条件');
    return true;
  }

  /**
   * 判断是否应该显示每月奖励弹窗
   * @param benefits 权益列表
   * @param forcePaymentReward 是否强制显示支付奖励
   */
  private shouldShowMonthlyReward(benefits: BenefitItem[], forcePaymentReward = false): boolean {
    // 安全检查
    if (!benefits || benefits.length === 0) {
      console.log('[用户权益] 无权益数据，不显示每月奖励');
      return false;
    }

    // 如果是强制支付奖励，只要有权益数据就显示（不限制每日次数）
    if (forcePaymentReward) {
      console.log('[用户权益] 支付奖励检查 - 权益数据:', {
        benefitsLength: benefits.length,
        benefits: benefits.map(b => ({ type: b.type, credits: b.credits })),
      });

      if (benefits.length === 0) {
        console.log('[用户权益] 支付完成但无权益数据，不显示奖励');
        return false;
      }

      console.log('[用户权益] 支付完成，强制显示奖励弹窗（支付后获得本月奖励）');
      return true;
    }

    // 正常的每月登录奖励逻辑
    // 首先检查是否是本月首次访问
    const isFirstVisitThisMonth = this.isThisMonthFirstVisit();
    if (!isFirstVisitThisMonth) {
      console.log('[用户权益] 本月已访问过，不显示每月奖励');
      return false;
    }

    // 检查是否有每月登录权益（type=3）- 这应该是服务端实时返回的数据
    const hasMonthlyReward = benefits.some(benefit => benefit.type === BenefitType.MONTHLY_REWARD);
    if (!hasMonthlyReward) {
      console.log('[用户权益] 服务端未返回每月登录权益数据，不显示每月奖励');
      return false;
    }

    console.log('[用户权益] 符合显示每月奖励条件，服务端已发放奖励');
    return true;
  }

  /**
   * 标记欢迎礼包已显示
   */
  public markWelcomeGiftShown(): void {
    try {
      localStorage.setItem(STORAGE_KEYS.WELCOME_GIFT_SHOWN, 'true');
      console.log('[用户权益] 欢迎礼包已标记为已显示');
    } catch (error) {
      console.error('[用户权益] 标记欢迎礼包状态失败:', error);
    }
  }

  /**
   * 更新每日奖励时间戳
   */
  public updateDailyRewardTimestamp(): void {
    try {
      const now = Date.now();
      localStorage.setItem(STORAGE_KEYS.DAILY_REWARD_TIMESTAMP, now.toString());
      console.log('[用户权益] 每日奖励时间戳已更新');
    } catch (error) {
      console.error('[用户权益] 更新每日奖励时间戳失败:', error);
    }
  }

  /**
   * 更新每月奖励时间戳
   */
  public updateMonthlyRewardTimestamp(): void {
    try {
      const now = Date.now();
      localStorage.setItem(STORAGE_KEYS.MONTHLY_REWARD_TIMESTAMP, now.toString());
      console.log('[用户权益] 每月奖励时间戳已更新');
    } catch (error) {
      console.error('[用户权益] 更新每月奖励时间戳失败:', error);
    }
  }

  /**
   * 更新支付奖励时间戳
   */
  public updatePaymentRewardTimestamp(): void {
    try {
      const now = Date.now();
      localStorage.setItem(STORAGE_KEYS.PAYMENT_REWARD_TIMESTAMP, now.toString());
      console.log('[用户权益] 支付奖励时间戳已更新');
    } catch (error) {
      console.error('[用户权益] 更新支付奖励时间戳失败:', error);
    }
  }

  /**
   * 标记手机号验证奖励已显示
   */
  public markPhoneVerificationRewardShown(): void {
    try {
      const now = Date.now();
      localStorage.setItem(STORAGE_KEYS.PHONE_VERIFICATION_REWARD_SHOWN, now.toString());
      console.log('[用户权益] 手机号验证奖励已标记为已显示');
    } catch (error) {
      console.error('[用户权益] 标记手机号验证奖励状态失败:', error);
    }
  }

  /**
   * 检查支付完成后的权益状态（专门用于支付成功后）
   * 支付完成后只显示支付奖励，不显示其他类型的奖励
   */
  public async checkPaymentRewardState(): Promise<ModalState> {
    console.log('[用户权益] 检查支付完成奖励状态');

    // 获取权益数据，强制刷新
    const benefitsData = await this.getUserBenefits(true);

    if (!benefitsData) {
      console.log('[用户权益] 支付完成但未获取到权益数据');
      return {
        showWelcomeGift: false,
        showDailyReward: false,
        showMonthlyReward: false,
        showPaymentReward: false,
        showPhoneVerificationReward: false,
        benefitsData: null,
      };
    }

    // 确保list字段存在
    const benefitsList = benefitsData.list || [];

    if (benefitsList.length === 0) {
      console.log('[用户权益] 支付完成但权益列表为空');
      return {
        showWelcomeGift: false,
        showDailyReward: false,
        showMonthlyReward: false,
        showPaymentReward: false,
        showPhoneVerificationReward: false,
        benefitsData: [],
      };
    }

    // 支付完成后，只显示每月奖励弹窗（作为支付奖励）
    // 不检查其他类型的奖励，避免重复弹窗
    const result = {
      showWelcomeGift: false, // 支付完成后不显示欢迎礼包
      showDailyReward: false, // 支付完成后不显示每日奖励
      showMonthlyReward: true, // 显示为支付完成奖励
      showPaymentReward: false,
      showPhoneVerificationReward: false, // 支付完成后不显示手机号验证奖励
      benefitsData: benefitsList,
    };

    console.log('[用户权益] 支付奖励检查结果:', {
      showMonthlyReward: result.showMonthlyReward,
      benefitsDataLength: result.benefitsData?.length || 0,
    });

    return result;
  }

  /**
   * 获取所有缓存的权益数据
   */
  private getAllCachedBenefitsData(): UserBenefitsResponse | null {
    try {
      // 检查localStorage是否可用
      if (!this.isLocalStorageAvailable()) {
        console.warn('[用户权益] localStorage不可用');
        return null;
      }

      const welcomeBenefits = this.getCachedBenefitsByType(1);
      const dailyBenefits = this.getCachedBenefitsByType(2);
      const monthlyBenefits = this.getCachedBenefitsByType(3);

      // 合并所有有效的缓存数据
      const allBenefits: BenefitItem[] = [];
      if (welcomeBenefits) allBenefits.push(...welcomeBenefits);
      if (dailyBenefits) allBenefits.push(...dailyBenefits);
      if (monthlyBenefits) allBenefits.push(...monthlyBenefits);

      if (allBenefits.length === 0) {
        return null;
      }

      return {
        list: allBenefits,
      };
    } catch (error) {
      console.error('[用户权益] 获取缓存数据失败:', error);
      return null;
    }
  }

  /**
   * 根据类型获取缓存的权益数据
   */
  private getCachedBenefitsByType(type: number): BenefitItem[] | null {
    try {
      let storageKey: string;
      switch (type) {
        case BenefitType.WELCOME_GIFT:
          storageKey = STORAGE_KEYS.WELCOME_BENEFITS_DATA;
          break;
        case BenefitType.DAILY_REWARD:
          storageKey = STORAGE_KEYS.DAILY_BENEFITS_DATA;
          break;
        case BenefitType.MONTHLY_REWARD:
          storageKey = STORAGE_KEYS.MONTHLY_BENEFITS_DATA;
          break;
        default:
          return null;
      }

      const cached = localStorage.getItem(storageKey);
      if (!cached) {
        return null;
      }

      let cachedData: CachedBenefitData;

      try {
        cachedData = JSON.parse(cached);
      } catch (parseError) {
        console.error(`[用户权益] 类型${type}缓存数据JSON解析失败:`, parseError);
        localStorage.removeItem(storageKey);
        return null;
      }

      // 验证缓存数据结构
      if (!cachedData || !cachedData.data || !cachedData.timestamp || !cachedData.ttl) {
        console.warn(`[用户权益] 类型${type}缓存数据结构无效`);
        localStorage.removeItem(storageKey);
        return null;
      }

      const now = Date.now();

      // 检查是否过期
      if (now - cachedData.timestamp > cachedData.ttl) {
        console.log(`[用户权益] 类型${type}缓存数据已过期`);
        localStorage.removeItem(storageKey);
        return null;
      }

      return cachedData.data;
    } catch (error) {
      console.error(`[用户权益] 获取类型${type}缓存数据失败:`, error);
      return null;
    }
  }

  /**
   * 检查localStorage是否可用
   */
  private isLocalStorageAvailable(): boolean {
    try {
      const testKey = '__test_storage__';
      localStorage.setItem(testKey, testKey);
      const result = localStorage.getItem(testKey) === testKey;
      localStorage.removeItem(testKey);
      return result;
    } catch (e) {
      return false;
    }
  }

  /**
   * 按类型设置缓存的权益数据
   */
  private setCachedBenefitsDataByType(data: UserBenefitsResponse): void {
    try {
      // 检查localStorage是否可用
      if (!this.isLocalStorageAvailable()) {
        console.warn('[用户权益] localStorage不可用，无法缓存数据');
        return;
      }

      // 检查数据有效性
      if (!data || !data.list) {
        console.warn('[用户权益] 无效的数据，跳过缓存');
        return;
      }

      // 按类型分组缓存
      const benefitsByType = new Map<number, BenefitItem[]>();

      data.list.forEach(benefit => {
        if (!benefitsByType.has(benefit.type)) {
          benefitsByType.set(benefit.type, []);
        }
        benefitsByType.get(benefit.type)!.push(benefit);
      });

      // 分别缓存每种类型的权益
      benefitsByType.forEach((benefits, type) => {
        this.setCachedBenefitsByType(type, benefits);
      });

      console.log('[用户权益] 权益数据已按类型分别缓存');
    } catch (error) {
      console.error('[用户权益] 按类型设置缓存数据失败:', error);
    }
  }

  /**
   * 设置特定类型的缓存权益数据
   */
  private setCachedBenefitsByType(type: number, benefits: BenefitItem[]): void {
    try {
      let storageKey: string;
      switch (type) {
        case BenefitType.WELCOME_GIFT:
          storageKey = STORAGE_KEYS.WELCOME_BENEFITS_DATA;
          break;
        case BenefitType.DAILY_REWARD:
          storageKey = STORAGE_KEYS.DAILY_BENEFITS_DATA;
          break;
        case BenefitType.MONTHLY_REWARD:
          storageKey = STORAGE_KEYS.MONTHLY_BENEFITS_DATA;
          break;
        default:
          console.warn(`[用户权益] 未知的权益类型: ${type}`);
          return;
      }

      const cachedData: CachedBenefitData = {
        data: benefits,
        timestamp: Date.now(),
        ttl: this.cacheTTL,
      };

      const serializedData = JSON.stringify(cachedData);
      localStorage.setItem(storageKey, serializedData);
      console.log(`[用户权益] 类型${type}权益数据已缓存`);
    } catch (error) {
      console.error(`[用户权益] 设置类型${type}缓存数据失败:`, error);
    }
  }

  /**
   * 获取欢迎礼包显示状态
   */
  private getWelcomeGiftShownStatus(): boolean {
    try {
      const shown = localStorage.getItem(STORAGE_KEYS.WELCOME_GIFT_SHOWN);
      return shown === 'true';
    } catch (error) {
      console.error('[用户权益] 获取欢迎礼包状态失败:', error);
      return false;
    }
  }

  /**
   * 检查是否是今天首次访问
   */
  private isTodayFirstVisit(): boolean {
    try {
      const lastTimestamp = localStorage.getItem(STORAGE_KEYS.DAILY_REWARD_TIMESTAMP);
      if (!lastTimestamp) {
        return true;
      }

      const lastDate = new Date(parseInt(lastTimestamp));
      const today = new Date();

      // 比较日期（忽略时间）
      return (
        lastDate.getFullYear() !== today.getFullYear() ||
        lastDate.getMonth() !== today.getMonth() ||
        lastDate.getDate() !== today.getDate()
      );
    } catch (error) {
      console.error('[用户权益] 检查首次访问状态失败:', error);
      return true;
    }
  }

  /**
   * 检查是否是本月首次访问
   */
  private isThisMonthFirstVisit(): boolean {
    try {
      const lastTimestamp = localStorage.getItem(STORAGE_KEYS.MONTHLY_REWARD_TIMESTAMP);
      if (!lastTimestamp) {
        return true;
      }

      const lastDate = new Date(parseInt(lastTimestamp));
      const today = new Date();

      // 比较年月（忽略日期和时间）
      return (
        lastDate.getFullYear() !== today.getFullYear() || lastDate.getMonth() !== today.getMonth()
      );
    } catch (error) {
      console.error('[用户权益] 检查本月首次访问状态失败:', error);
      return true;
    }
  }

  /**
   * 检查是否是今天首次支付
   */
  private isTodayFirstPayment(): boolean {
    try {
      const lastTimestamp = localStorage.getItem(STORAGE_KEYS.PAYMENT_REWARD_TIMESTAMP);
      if (!lastTimestamp) {
        return true;
      }

      const lastDate = new Date(parseInt(lastTimestamp));
      const today = new Date();

      // 比较日期（忽略时间）
      return (
        lastDate.getFullYear() !== today.getFullYear() ||
        lastDate.getMonth() !== today.getMonth() ||
        lastDate.getDate() !== today.getDate()
      );
    } catch (error) {
      console.error('[用户权益] 检查今日首次支付状态失败:', error);
      return true;
    }
  }

  /**
   * 清理所有权益相关的缓存数据
   */
  public clearAllCache(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.WELCOME_BENEFITS_DATA);
      localStorage.removeItem(STORAGE_KEYS.DAILY_BENEFITS_DATA);
      localStorage.removeItem(STORAGE_KEYS.MONTHLY_BENEFITS_DATA);
      localStorage.removeItem(STORAGE_KEYS.WELCOME_GIFT_SHOWN);
      localStorage.removeItem(STORAGE_KEYS.DAILY_REWARD_TIMESTAMP);
      localStorage.removeItem(STORAGE_KEYS.MONTHLY_REWARD_TIMESTAMP);
      localStorage.removeItem(STORAGE_KEYS.PAYMENT_REWARD_TIMESTAMP);
      console.log('[用户权益] 所有缓存数据已清理');
    } catch (error) {
      console.error('[用户权益] 清理缓存失败:', error);
    }
  }

  /**
   * 获取权益数据的统计信息（用于调试）
   */
  public getDebugInfo(): object {
    try {
      const cachedData = this.getAllCachedBenefitsData();
      const welcomeShown = this.getWelcomeGiftShownStatus();
      const isFirstVisit = this.isTodayFirstVisit();
      const isFirstVisitThisMonth = this.isThisMonthFirstVisit();
      const isFirstPaymentToday = this.isTodayFirstPayment();

      return {
        hasCachedData: !!cachedData,
        cachedDataCount: cachedData?.list?.length || 0,
        welcomeGiftShown: welcomeShown,
        isTodayFirstVisit: isFirstVisit,
        isThisMonthFirstVisit: isFirstVisitThisMonth,
        isTodayFirstPayment: isFirstPaymentToday,
        isRequesting: this.isRequesting,
      };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }
}

// 导出单例实例
export const userBenefitsManager = UserBenefitsManager.getInstance();
export default userBenefitsManager;
